import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Card from '@mui/material/Card/Card';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Chip from '@mui/material/Chip/Chip';
import Collapse from '@mui/material/Collapse/Collapse';
import { useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { getOrganizationHistory, OrgHistory, OrgPastHackathon } from '../../services/organization.service';
import { formatDateRange } from '../../utils/hackathonStatus';
import { BrandedThemeProvider } from '../Theme/AppThemeProvider';

/** One past event of the organization: what happened, and a link to look around it (read-only, you weren't in it). */
const PastEventCard = ({ event }: { event: OrgPastHackathon }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const dates = formatDateRange(event.startDate ? new Date(event.startDate) : null, event.endDate ? new Date(event.endDate) : null);
    return (
        <Card variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
                <Box>
                    <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}><Link to={`/h/${event.slug}`} style={{ color: 'inherit' }}>{event.name}</Link></Typography>
                    <Typography variant="body2" color="text.secondary">
                        {[dates, `${event.participantCount} ${event.participantCount === 1 ? 'person' : 'people'}`, `${event.ideaCount} ${event.ideaCount === 1 ? 'idea' : 'ideas'}`].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                {
                    event.winners.length > 0 && <Stack spacing={0.25}>
                        {event.winners.map((w, i) => (
                            <Stack key={i} direction="row" spacing={0.75} alignItems="center">
                                <EmojiEventsIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}><b>{w.category}:</b> {w.title}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                }
                {(!!event.description?.trim() || event.categories.length > 0) && <Box>
                    <Button size="small" color="inherit" sx={{ px: 0.5, minWidth: 0, color: 'text.secondary' }} aria-expanded={open}
                        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setOpen(o => !o)}>
                        {open ? 'Less' : 'More about it'}
                    </Button>
                    <Collapse in={open} unmountOnExit>
                        <Stack spacing={1} sx={{ pt: 0.5 }}>
                            {event.description?.trim() && <Box sx={{ fontSize: 14, '& p': { my: 0.5 } }}><Markdown>{event.description}</Markdown></Box>}
                            {
                                event.categories.length > 0 && <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {event.categories.map(c => <Chip key={c.name} size="small" variant="outlined" label={c.judgeOnly ? `${c.name} · judges only` : c.name} />)}
                                </Stack>
                            }
                        </Stack>
                    </Collapse>
                </Box>}
            </Stack>
        </Card>
    );
};

/**
 * "More from <organization>": past hackathons of the organizations you've been part of that you weren't in, so
 * people find out what their company has run before.
 */
export const OrganizationHistory = () => {
    const [groups, setGroups] = useState<OrgHistory[]>([]);
    useEffect(() => {
        getOrganizationHistory().then(setGroups, err => console.error(err));
    }, []);

    if (groups.length === 0) {
        return null;
    }
    return (
        <Stack spacing={3}>
            {groups.map(group => (
                <BrandedThemeProvider key={group.organization._id} branding={group.organization.branding}>
                    <Stack spacing={1.5}>
                        <Box>
                            <Typography variant="overline" color="text.secondary">More from {group.organization.name}</Typography>
                            <Typography variant="body2" color="text.secondary">Past hackathons at {group.organization.name} you weren't part of.</Typography>
                        </Box>
                        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, alignItems: 'start' }}>
                            {group.hackathons.map(event => <PastEventCard key={event._id} event={event} />)}
                        </Box>
                    </Stack>
                </BrandedThemeProvider>
            ))}
        </Stack>
    );
};
